import React from 'react';
import { Card, CardSkeleton } from '@/components/ui';
import type { FeedbackMetrics } from '@/types/feedback';

interface FeedbackByStepCardProps {
  metrics: FeedbackMetrics | null;
  loading: boolean;
}

const STEP_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  design: 'Design',
  multiagent: 'Architecture',
  technical: 'Technical',
  codegen: 'Code Gen',
  operations: 'Operations',
};

export const FeedbackByStepCard: React.FC<FeedbackByStepCardProps> = ({
  metrics,
  loading,
}) => {
  if (loading) {
    return <CardSkeleton />;
  }

  if (!metrics) {
    return (
      <Card padding="lg">
        <p className="text-secondary-500">No step breakdown available</p>
      </Card>
    );
  }

  const feedbackByStep = metrics.feedback_by_step || {};
  const stepEntries = Object.entries(feedbackByStep);

  return (
    <Card padding="lg">
      <h3 className="text-lg font-semibold text-secondary-900 mb-4">
        Feedback by Step
      </h3>

      {stepEntries.length === 0 ? (
        <p className="text-secondary-500">No feedback data by step yet</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Step
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Positive
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Negative
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Satisfaction
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200">
              {stepEntries.map(([step, stepData]) => {
                const total = stepData.positive + stepData.negative;
                const ratio =
                  total > 0 ? Math.round((stepData.positive / total) * 100) : 0;

                return (
                  <tr key={step} className="hover:bg-secondary-50">
                    <td className="px-4 py-3 text-sm font-medium text-secondary-900">
                      {STEP_LABELS[step] || step}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {stepData.positive}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {stepData.negative}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center font-medium text-secondary-700">
                      {total}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-secondary-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              ratio >= 70
                                ? 'bg-green-500'
                                : ratio >= 50
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                            }`}
                            style={{ width: `${ratio}%` }}
                          />
                        </div>
                        <span
                          className={`text-xs font-medium ${
                            ratio >= 70
                              ? 'text-green-600'
                              : ratio >= 50
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}
                        >
                          {ratio}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default FeedbackByStepCard;
